/**
 * 게시판 — 게시글 목록 + 정렬 + 글쓰기 버튼.
 *
 * 영구지침: docs/develop/community/post.md §7
 */
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Heart, MessageCircle, Eye, Check, Pencil, ChevronLeft, Lock, Megaphone, Globe, Paperclip } from 'lucide-react'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import StickySideBanner from '@/components/common/StickySideBanner'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import { useQueryClient } from '@tanstack/react-query'
import {
  useBoardPosts,
  useCommunityBoard,
  type SortKey,
} from '@/features/community/publicApi'

const SORT_LABELS: Record<SortKey, string> = {
  recent: '최신순',
  popular: '좋아요순',
  comments: '댓글순',
  views: '조회순',
}

export default function BoardPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const qc = useQueryClient()

  const [sort, setSort] = useState<SortKey>('recent')
  const { data: board } = useCommunityBoard(slug)
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBoardPosts(slug, sort)

  const posts = data?.pages.flatMap((p) => p.content) ?? []

  const handleWrite = () => {
    if (!slug) return
    if (!isAuthenticated) {
      toast.info('글을 작성하려면 로그인이 필요합니다')
      navigate('/login', { state: { from: `/community/${slug}` } })
      return
    }
    // ADMIN/SUPER_ADMIN 은 캐시된 myPermissions 가 비로그인 상태일 수 있으므로 우선 이동.
    // 서버가 최종 권한 판정을 하므로 안전.
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
    if (!isAdmin && !board?.myPermissions.canWritePost) {
      toast.error('이 게시판에는 글을 작성할 수 없습니다')
      qc.invalidateQueries({ queryKey: ['community-board', slug] })
      return
    }
    navigate(`/community/${slug}/write`)
  }

  return (
    <>
      {/* 게시판 목록 좌우 세로배너(160x600) — 콘텐츠(max-w-3xl=768) 바로 옆 여백에.
          초광폭(xl=1280px↑)에서만, 푸터 위에서 멈춤(StickySideBanner). */}
      <StickySideBanner slotKey="community_board_side_left" side="left" topPx={96} edgeOffsetPx={592} breakpoint="xl" />
      <StickySideBanner slotKey="community_board_side_right" side="right" topPx={96} edgeOffsetPx={592} breakpoint="xl" />
      {/* 글이 적으면 본문이 짧아 푸터가 올라오고, 600px 세로배너가 들어갈 자리가 없어진다.
          배너 높이(600) + 상단 여백(96) + 푸터 간격(16) 을 담을 최소 높이를 확보한다. */}
      <main className="mx-auto min-h-[712px] w-full max-w-3xl px-4 py-6">
      {/* 모든 게시판에 고유 title·canonical (board-cms §6.4, ssr-spec §15.4).
          '정보 공유'(info — KOGL 공공저작물 자동수집 전용) 게시판 목록은 중복 콘텐츠라 색인 제외 */}
      <SEO
        title={`${board?.name ?? (slug === 'info' ? '정보 공유' : '게시판')} - 보호자 커뮤니티`}
        description={
          board?.description
          || `${board?.name ?? '게시판'} — 부모님을 모시는 보호자들이 요양 경험과 정보를 나누는 케어밸런스 커뮤니티입니다.`
        }
        url={`/community/${encodeURIComponent(slug ?? '')}`}
        noindex={slug === 'info'}
      />
      <button
        type="button"
        onClick={() => navigate('/community')}
        className="mb-4 inline-flex items-center gap-1 text-caption text-cb-gray-500 hover:text-cb-gray-900"
      >
        <ChevronLeft size={14} /> 커뮤니티
      </button>

      <header className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="text-heading-md font-bold text-cb-gray-900">{board?.name ?? '...'}</h1>
          {board?.description && (
            <p className="mt-1 text-body-sm text-cb-gray-500">{board.description}</p>
          )}
        </div>
        <Button onClick={handleWrite}>
          <Pencil size={16} className="mr-1" /> 글쓰기
        </Button>
      </header>

      {/* 정렬 탭 */}
      <div className="mb-4 flex gap-2 border-b border-cb-gray-200">
        {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setSort(key)}
            className={`border-b-2 px-3 py-2 text-caption font-medium transition-colors ${
              sort === key
                ? 'border-cb-primary text-cb-primary'
                : 'border-transparent text-cb-gray-500 hover:text-cb-gray-900'
            }`}
          >
            {SORT_LABELS[key]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-cb-gray-200 bg-white p-12 text-center text-cb-gray-500">
          아직 게시글이 없습니다. 첫 글을 작성해보세요.
        </div>
      ) : (
        <ul className="divide-y divide-cb-gray-100 rounded-2xl border border-cb-gray-200 bg-white">
          {posts.map((p) => (
            <li key={p.id}>
              <Link
                to={`/community/posts/${p.id}`}
                className={`block px-4 py-4 transition-colors hover:bg-cb-gray-50 ${
                  p.isGlobalNotice || p.isNotice ? 'bg-cb-primary-light/20' : ''
                }`}
              >
                <div className="mb-1 flex items-start justify-between gap-2">
                  <h2 className="flex-1 text-body-md font-semibold text-cb-gray-900">
                    {p.isGlobalNotice && (
                      <span className="mr-1 inline-flex items-center rounded bg-cb-primary px-1.5 py-0.5 text-caption font-medium text-white">
                        <Globe size={11} className="mr-0.5" />
                        전체공지
                      </span>
                    )}
                    {p.isNotice && !p.isGlobalNotice && (
                      <span className="mr-1 inline-flex items-center rounded bg-cb-primary-light px-1.5 py-0.5 text-caption font-medium text-cb-primary">
                        <Megaphone size={11} className="mr-0.5" />
                        공지
                      </span>
                    )}
                    {p.hasAcceptedAnswer && (
                      <span className="mr-1 inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-caption font-medium text-green-700">
                        <Check size={12} className="mr-0.5" />
                        채택
                      </span>
                    )}
                    {p.isSecret && (
                      <span className="mr-1 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-caption font-medium text-amber-700">
                        <Lock size={11} className="mr-0.5" />
                        비밀글
                      </span>
                    )}
                    {p.title}
                    {p.isGlobalNotice && p.board && p.board.slug !== slug && (
                      <span className="ml-2 text-caption font-normal text-cb-gray-400">
                        · {p.board.name}
                      </span>
                    )}
                  </h2>
                  <span className="shrink-0 text-caption text-cb-gray-400">
                    {formatRelativeTime(p.createdAt)}
                  </span>
                </div>
                {p.excerpt && (
                  <p className="line-clamp-2 text-caption text-cb-gray-500">{p.excerpt}</p>
                )}
                <div className="mt-2 flex items-center gap-3 text-caption text-cb-gray-400">
                  <span>{p.author.nickname}</span>
                  <span className="inline-flex items-center gap-1">
                    <Heart size={12} /> {p.likeCount}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MessageCircle size={12} /> {p.commentCount}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Eye size={12} /> {p.viewCount}
                  </span>
                  {p.attachmentCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Paperclip size={12} /> {p.attachmentCount}
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <div className="mt-6 flex justify-center">
          <Button variant="ghost" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? '불러오는 중...' : '더 보기'}
          </Button>
        </div>
      )}
      </main>
    </>
  )
}

function formatRelativeTime(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diff = Math.max(0, now - then)
  const min = Math.floor(diff / 60000)
  if (min < 1) return '방금'
  if (min < 60) return `${min}분 전`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}시간 전`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}일 전`
  return new Date(iso).toLocaleDateString('ko-KR')
}
